import { useState, useEffect } from 'react';
import { useSearchParams } from 'react-router-dom';
import { User, Shield, Store, Users, CreditCard, Receipt, Settings, AlertTriangle, Mail, Palette } from 'lucide-react';
import { IdentitySection } from './IdentitySection';
import { SecuritySection } from './SecuritySection';
import { AppearanceSection } from './AppearanceSection';
import { ShopIdentitySection } from './ShopIdentitySection';
import { TeamManagementSection } from './TeamManagementSection';
import { SubscriptionSection } from './SubscriptionSection';
import { FiscalSettingsSection } from './FiscalSettingsSection';
import { PricingSettingsSection } from './PricingSettingsSection';
import { EmailTemplatesSection } from './EmailTemplatesSection';
import { DangerZoneSection } from './DangerZoneSection';
import { DataExportSection } from './DataExportSection';
import { useRole } from '../../context/RoleContext';

type Tab = 'details' | 'security' | 'appearance' | 'shop' | 'pricing' | 'emails' | 'team' | 'subscription' | 'fiscal' | 'advanced';

const VALID_TABS: Tab[] = ['details', 'security', 'appearance', 'shop', 'pricing', 'emails', 'team', 'subscription', 'fiscal', 'advanced'];

export const ProfilePage = () => {
    const [searchParams, setSearchParams] = useSearchParams();
    const urlTab = searchParams.get('tab') as Tab;
    
    // State to act as the single source of truth for UI, initializing from URL if valid
    const [activeTab, setActiveTab] = useState<Tab>(
        VALID_TABS.includes(urlTab) ? urlTab : 'details'
    );

    // Sync state if URL changes (e.g. via Stripe Connect/Checkout redirect)
    useEffect(() => {
        if (urlTab && VALID_TABS.includes(urlTab)) {
            setActiveTab(urlTab as Tab);
        }
    }, [urlTab]);

    // Sync state to URL without full refresh when tab changes
    const handleTabChange = (tab: Tab) => {
        setActiveTab(tab);
        setSearchParams({ tab, ...(searchParams.get('session_id') ? { session_id: searchParams.get('session_id')! } : {}) });
    };

    const { can } = useRole();

    const showBusinessTabs = can('team_management') || can('billing');

    const navButton = (tab: Tab, label: string, Icon: React.ElementType) => (
        <button
            key={tab}
            onClick={() => handleTabChange(tab)}
            className={`w-full flex items-center gap-3 px-4 py-3 rounded-[var(--fds-radius)] text-sm font-medium transition-colors ${activeTab === tab ? 'bg-raised text-fg border-l-2 border-accent' : 'text-fg-2 hover:bg-raised'}`}
        >
            <Icon className="w-4 h-4" />
            {label}
        </button>
    );

    return (
        <div className="w-full space-y-8 animate-in fade-in duration-500">
            {/* Header */}
            <div className="flex flex-col md:flex-row md:items-end justify-between gap-6 pb-6 border-b border-line">
                <div className="flex items-center gap-3">
                    <div className="p-3 bg-accent/10 rounded-xl">
                        <Settings className="w-6 h-6 text-accent" />
                    </div>
                    <div>
                        <h1 className="text-3xl font-bold text-fg tracking-tight">
                            Settings
                        </h1>
                        <p className="text-fg-2 mt-1 max-w-2xl">
                            Manage your profile, business identity, team, and fiscal configuration.
                        </p>
                    </div>
                </div>
            </div>

            {/* Layout */}
            <div className="grid grid-cols-1 md:grid-cols-4 gap-8">
                {/* Side Nav */}
                <nav className="flex flex-col space-y-2">
                    <p className="px-4 text-xs font-medium text-fg-2 uppercase tracking-wider mb-2">Personal</p>
                    {navButton('details', 'Personal Details', User)}
                    {navButton('security', 'Security & Login', Shield)}
                    {navButton('appearance', 'Appearance', Palette)}

                    {showBusinessTabs && (
                        <>
                            <div className="pt-4" />
                            <p className="px-4 text-xs font-medium text-fg-2 uppercase tracking-wider mb-2">Business</p>
                            {can('team_management') && navButton('shop', 'Shop Identity', Store)}
                            {can('team_management') && navButton('pricing', 'Pricing & Rates', Receipt)}
                            {can('team_management') && navButton('emails', 'Email Templates', Mail)}
                            {can('team_management') && navButton('team', 'Team Management', Users)}
                            {can('billing') && navButton('subscription', 'Subscription', CreditCard)}
                            {can('billing') && navButton('fiscal', 'Tax & VAT', Receipt)}
                            {can('team_management') && navButton('advanced', 'Advanced', AlertTriangle)}
                        </>
                    )}
                </nav>

                {/* Content */}
                <div className="md:col-span-3 bg-panel border border-line rounded-[var(--fds-radius)] p-6 min-h-[400px]">
                    {activeTab === 'details' && <IdentitySection />}
                    {activeTab === 'security' && <SecuritySection />}
                    {activeTab === 'appearance' && <AppearanceSection />}
                    {activeTab === 'shop' && <ShopIdentitySection />}
                    {activeTab === 'pricing' && <PricingSettingsSection />}
                    {activeTab === 'emails' && <EmailTemplatesSection />}
                    {activeTab === 'team' && <TeamManagementSection />}
                    {activeTab === 'subscription' && <SubscriptionSection />}
                    {activeTab === 'fiscal' && <FiscalSettingsSection />}
                    {activeTab === 'advanced' && (
                        <div className="space-y-12">
                            <DataExportSection />
                            <DangerZoneSection />
                        </div>
                    )}
                </div>
            </div>
        </div>
    );
};
